// Package imports:
import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/pro-solid-svg-icons';
import { format } from 'date-fns';
import isIsLocale from 'date-fns/locale/is';
import enGBLocale from 'date-fns/locale/en-GB';
import DOMPurify from 'dompurify';
// Component imports:
import Alert from '../../ui-elements/Alert/Alert';
import DisplayBox from '../../ui-elements/DisplayBox/DisplayBox';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import LanguagePicker from '../../ui-elements/LanguagePicker/LanguagePicker';
import Label from '../../ui-elements/Label/Label';
import NewsOrgInfo from '../NewsOrgInfo/NewsOrgInfo';
import Link from '../../ui-elements/Link/Link';
import SocialMediaSharing from '../SocialMediaSharing/SocialMediaSharing';
import NewsAttachments from '../NewsAttachments/NewsAttachments';
// Type imports:
import { INewsLmdNewsItem } from '../../types/NewsTypes';
import AdRotator from '../Ad/AdRotator';

interface IProps {
    id: string,
    newsItem: INewsLmdNewsItem | null
}

const ViewNewsArticle: React.FC<IProps> = ({
    id,
    newsItem
}) => {
    const [newsId, setNewsId] = useState(id);
    const [lang, setLang] = useState('IS');

    const cleanHtmlTitle = useMemo(() => {
        return DOMPurify.sanitize(newsItem?.title ?? '')
    }, [newsItem])

    const cleanHtml = useMemo(() => {
        return DOMPurify.sanitize(newsItem?.bodyHtml ?? '')
    }, [newsItem])

    const displayBody = () => {
        if (newsItem === null) {
            return (
                <Alert type='alert' headText='Ekki fundust gögn fyrir þessa frétt' />
            )
        }
        return (<>
            <HeadSection
                breadcrumbs={
                    <Link href="/Kauphallartilkynningar" icon='back' linkSize='14'>
                        Allar kauphallartilkynningar
                    </Link>
                }
                hasAds='main'
            />
            <div className='grid-items'>
                <div className='grid-item grid-item--3of4 grid-item--table-full'>
                    <DisplayBox
                        titleComponent={<span dangerouslySetInnerHTML={{ __html: cleanHtmlTitle }} />}
                        asideComponent={
                            newsItem.writtenInLang.length > 1
                                ? <LanguagePicker
                                    availableLanguages={
                                        newsItem.writtenInLang.map(l => ({ lang: l.lang, value: l.linkId }))
                                    }
                                    defaultValue={decodeURI(newsId)}
                                    onChange={(val) => {
                                        setNewsId(val ?? '')
                                    }}
                                    setLanguage={setLang}
                                />
                                : undefined
                        }
                        displayBoxSize='xxl'
                        footerLeft={
                            <div className='origin-wrapper'>
                                <span className='origin-label'>
                                    {lang === 'IS' ? 'HEIMILD' : 'SOURCE'}
                                </span>
                                <span className='extra-info'>
                                    {newsItem.distributor?.toLowerCase() === 'globenewswire' || newsItem.distributor?.toLowerCase() === 'mfn'
                                        ? <a href={newsItem.link ?? ''} target='_blank' rel='noreferrer'>
                                            {newsItem.distributor}
                                        </a>
                                        : newsItem.feedMeta?.sourceName
                                    }
                                </span>
                            </div>
                        }
                        footerRight={
                            <SocialMediaSharing dataTitle={newsItem.title ?? ''} />
                        }
                    >
                        <div className='time-and-category'>
                            <span>
                                {format(
                                    new Date(newsItem.publish_timestamp ?? 0),
                                    'PPPpp',
                                    { locale: lang === 'IS' ? isIsLocale : enGBLocale }
                                )}
                            </span>
                            <span className='source'>{newsItem.feedMeta?.sourceName}</span>
                            {newsItem.feedMeta?.feedType === 'press_release' && (
                                lang === 'IS'
                                    ? <Label
                                        labelType='news-release'
                                        text='Fréttatilkynning'
                                        size='lg'
                                    />
                                    : <Label
                                        labelType='news-release'
                                        text='Press release'
                                        size='lg'
                                    />
                            )}
                            {newsItem.feedMeta?.feedType === 'exchange_notice' && (
                                lang === 'IS'
                                    ? <Label
                                        labelType='news-release'
                                        text='Kauphallartilkynning'
                                        size='lg'
                                    />
                                    : <Label
                                        labelType='news-release'
                                        text='Exchange notice'
                                        size='lg'
                                    />
                            )}
                            <button onClick={() => window.print()} className='print-button' title='Prenta'>
                                <FontAwesomeIcon icon={faPrint} />
                            </button>
                        </div>
                        <div className='news-body'>
                            <div className="news-entry paragraph" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
                        </div>
                    </DisplayBox>
                </div>
                <div className='grid-item grid-item--1of4 grid-item--table-full'>
                    <NewsOrgInfo
                        symbol={newsItem.feedMeta?.sourceKey ?? ''}
                        feedKey={newsItem.feedMeta?.feedKey ?? ''}
                        sourceKey={newsItem.feedMeta?.sourceKey ?? ''}
                    />
                    {(newsItem.distributor === 'newswireIS' || newsItem.distributor === 'newswireEN') &&
                        <NewsAttachments attachments={newsItem.attachments} />
                    }
                    <AdRotator location={'FrontPage310x400A'} />
                </div>
            </div>
        </>)
    }

    return <div className='main KCL_view-news-article'>
        <div className="main__inner-fluid">
            <div className="shell">
                {displayBody()}
            </div>
        </div>
    </div>
}

export default ViewNewsArticle;
